import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import { getJobs } from '../redux/actions/job.action';
import { fetchCompanyData } from '../redux/actions/auth.action';
import PlatformAvatar from '../components/platform/PlatformAvatar';
import PlatformBadge from '../components/platform/PlatformBadge';
import PlatformButton from '../components/platform/PlatformButton';
import PlatformIcon from '../components/platform/PlatformIcon';
import '../styles/platform-dashboard.css';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const getLastSixMonths = (referenceDate = new Date()) => {
  const currentMonthIndex = referenceDate.getMonth();

  return Array.from({ length: 6 }, (_, index) => {
    const monthIndex = (currentMonthIndex - 5 + index + 12) % 12;

    return {
      label: MONTH_LABELS[monthIndex],
      key: index + 1,
    };
  });
};

const getMonthlyActivityValue = (monthlyActivity, key) =>
  Number(monthlyActivity?.[key] ?? monthlyActivity?.[String(key)] ?? 0);

const mergeMetricSources = (...sources) => {
  const emailDistribution = {};
  const monthlyActivity = {};

  sources.filter(Boolean).forEach((source) => {
    Object.assign(emailDistribution, source.emailDistribution || {});
    Object.assign(monthlyActivity, source.monthlyActivity || {});
  });

  return { emailDistribution, monthlyActivity };
};

function StatCard({ icon, tone, value, label, sub, onClick }) {
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className={`platform-card dashboard-stat ${onClick ? 'dashboard-stat--clickable' : ''}`}
      onClick={onClick}
    >
      <div className="platform-row" style={{ justifyContent: 'space-between' }}>
        <div className={`dashboard-stat-icon dashboard-stat-icon--${tone}`}>
          <PlatformIcon name={icon} size={21} />
        </div>
      </div>
      <div>
        <div className="dashboard-stat-value">{value}</div>
        <div className="dashboard-stat-label">{label}</div>
        {sub && <div className="dashboard-stat-sub">{sub}</div>}
      </div>
    </Tag>
  );
}

const DashboardHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, company } = useSelector((state) => state.auth || {});
  const { jobs } = useSelector((state) => state.jobs);

  const { emailDistribution, monthlyActivity } = mergeMetricSources(company, user);
  const touchesCount = Number(emailDistribution.touches || 0);
  const eventsCount = Number(emailDistribution.events || 0);
  const newslettersCount = Number(emailDistribution.newsletters || 0);
  const totalMessages = touchesCount + eventsCount + newslettersCount;
  const hasEmailDistribution = totalMessages > 0;
  const emailDistributionSeries = hasEmailDistribution
    ? [touchesCount, eventsCount, newslettersCount]
    : [1];
  const emailDistributionLabels = hasEmailDistribution
    ? ['Touches', 'Events', 'Newsletters']
    : ['No activity yet'];
  const emailDistributionColors = hasEmailDistribution
    ? ['#0C8587', '#DD9F3C', '#23C2C4']
    : ['#D8D3C8'];

  const [filteredJobs, setFilteredJobs] = useState([]);
  const monthlyActivityMonths = getLastSixMonths();
  const monthlyActivityData = monthlyActivityMonths.map(({ key }) =>
    getMonthlyActivityValue(monthlyActivity, key)
  );
  const currentMonthActivity = monthlyActivityData[monthlyActivityData.length - 1] || 0;

  useEffect(() => {
    dispatch(getJobs());
    dispatch(fetchCompanyData());
  }, [dispatch]);

  useEffect(() => {
    if (user?.companyID && jobs?.length > 0) {
      setFilteredJobs(jobs.filter((job) => job.companyID === user.companyID));
    } else if (jobs?.length > 0) {
      setFilteredJobs(jobs);
    } else {
      setFilteredJobs([]);
    }
  }, [jobs, user?.companyID]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const touchpointData = useMemo(() => {
    if (!company?.tickets?.length) {
      return [];
    }

    const allMessages = [...company.tickets].sort((a, b) => {
      if (a.sentOn && b.sentOn) {
        return b.sentOn - a.sentOn;
      }
      return 0;
    });

    return allMessages.slice(0, 5).map((msg, idx) => ({
      id: msg.id || idx,
      title: msg.messageTitle || msg.subject || 'No Title',
      subtitle: msg.messageSender
        ? `${msg.messageSender} · ${msg.messageSenderEmail || ''}`
        : (msg.messageSenderEmail || 'System'),
      status: (msg.status && msg.status.toLowerCase() === 'pending') ? 'Pending' : (msg.messageStatus || 'Sent'),
      isPending: !msg.messageStatus || msg.messageStatus.toLowerCase() === 'pending',
    }));
  }, [company?.tickets]);

  const recentUsers = useMemo(
    () => filteredJobs.slice(0, 5),
    [filteredJobs]
  );

  const displayName = user?.displayName || user?.name || user?.firstName || 'there';
  const firstName = displayName.split(' ')[0] || displayName;
  const hour = new Date().getHours();
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const dateLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const pendingCount = touchpointData.filter((item) => item.isPending).length;

  return (
    <div className="nurturer-platform">
      <div className="dashboard-view platform-fade">
        <div className="platform-card dashboard-hero">
          <div className="dashboard-hero-glow" aria-hidden="true" />
          <div style={{ position: 'relative' }}>
            <div className="dashboard-hero-date">{dateLabel}</div>
            <h2 className="dashboard-hero-title">
              {greet}, {firstName}.
            </h2>
            <p className="dashboard-hero-copy">
              You&apos;re managing <strong style={{ color: '#fff' }}>{filteredJobs.length} nurturer users</strong>
              {pendingCount > 0 && (
                <>
                  {' '}with <strong style={{ color: '#fff' }}>{pendingCount} pending messages</strong>
                </>
              )}
              . Monitor outreach activity, email distribution, and user engagement from here.
            </p>
          </div>
          <div style={{ position: 'relative' }}>
            <PlatformButton variant="accent" size="lg" icon="sparkle" onClick={() => navigate('/apps/inbox')}>
              View inbox
            </PlatformButton>
          </div>
        </div>

        <div className="dashboard-stats">
          <StatCard
            icon="contacts"
            tone="primary"
            value={filteredJobs.length}
            label="Total users"
            sub="Nurturer accounts in your org"
            onClick={() => navigate('/candidates')}
          />
          <StatCard
            icon="clock"
            tone="accent"
            value={pendingCount}
            label="Pending messages"
            sub="Awaiting review or delivery"
            onClick={() => navigate('/apps/inbox')}
          />
          <StatCard
            icon="send"
            tone="info"
            value={totalMessages}
            label="Messages sent"
            sub="Touches, events & newsletters"
          />
          <StatCard
            icon="chart"
            tone="rose"
            value={currentMonthActivity}
            label="This month"
            sub="Rolling activity count"
          />
        </div>

        <div className="dashboard-charts">
          <div className="platform-card dashboard-chart-panel">
            <h3 className="dashboard-chart-title">Email distribution</h3>
            <ReactApexChart
              key={`donut-${totalMessages}`}
              options={{
                chart: {
                  type: 'donut',
                  foreColor: '#6C766F',
                  toolbar: { show: false },
                },
                labels: emailDistributionLabels,
                colors: emailDistributionColors,
                legend: {
                  position: 'bottom',
                  horizontalAlign: 'center',
                  offsetY: 4,
                },
                tooltip: {
                  y: {
                    formatter: (value, { seriesIndex }) => {
                      if (!hasEmailDistribution) {
                        return '0 messages';
                      }

                      const counts = [touchesCount, eventsCount, newslettersCount];
                      return `${counts[seriesIndex] ?? 0} messages`;
                    },
                  },
                },
                dataLabels: {
                  enabled: hasEmailDistribution,
                  formatter: (val) => `${Math.round(val)}%`,
                  style: {
                    fontSize: '13px',
                    fontFamily: 'DM Sans, sans-serif',
                    colors: ['#fff'],
                  },
                },
                plotOptions: {
                  pie: {
                    donut: {
                      labels: {
                        show: true,
                        total: {
                          show: true,
                          label: 'Total',
                          formatter: () => `${totalMessages}`,
                        },
                      },
                    },
                  },
                },
              }}
              series={emailDistributionSeries}
              type="donut"
              height={300}
            />
          </div>

          <div className="platform-card dashboard-chart-panel">
            <h3 className="dashboard-chart-title">Monthly activity</h3>
            <ReactApexChart
              key={`bar-${monthlyActivityData.join('-')}`}
              options={{
                chart: { type: 'bar', toolbar: { show: false }, foreColor: '#6C766F' },
                xaxis: { categories: monthlyActivityMonths.map(({ label }) => label) },
                yaxis: {
                  min: 0,
                  forceNiceScale: true,
                  labels: {
                    formatter: (value) => Math.round(value),
                  },
                },
                colors: ['#0C8587'],
                plotOptions: {
                  bar: { borderRadius: 6, horizontal: false, columnWidth: '55%' },
                },
                grid: {
                  borderColor: 'rgba(28, 40, 33, 0.08)',
                },
                dataLabels: { enabled: false },
                tooltip: {
                  y: {
                    formatter: (value) => `${Math.round(value)} messages`,
                  },
                },
              }}
              series={[{
                name: 'Activity',
                data: monthlyActivityData,
              }]}
              type="bar"
              height={300}
            />
          </div>
        </div>

        <div className="dashboard-columns">
          <div className="platform-card dashboard-panel">
            <div className="platform-row dashboard-panel-head">
              <div className="platform-row" style={{ gap: 10 }}>
                <h3 className="dashboard-panel-title">Recent messages</h3>
                {pendingCount > 0 && (
                  <PlatformBadge tone="pending">{pendingCount} pending</PlatformBadge>
                )}
              </div>
              <PlatformButton variant="link" icon="chevR" onClick={() => navigate('/apps/inbox')}>
                View all
              </PlatformButton>
            </div>
            <div>
              {touchpointData.length === 0 ? (
                <p className="dashboard-empty">No recent messages found.</p>
              ) : (
                touchpointData.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="platform-row dashboard-touch-row"
                    onClick={() => navigate('/apps/inbox')}
                  >
                    <div className="dashboard-touch-icon">
                      <PlatformIcon name="mail" size={18} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="platform-clip" style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)' }}>
                        {item.title}
                      </div>
                      <div className="platform-clip" style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>
                        {item.subtitle}
                      </div>
                    </div>
                    <PlatformBadge tone={item.isPending ? 'pending' : 'sent'} dot={item.isPending}>
                      {item.status}
                    </PlatformBadge>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="platform-card dashboard-panel">
            <div className="platform-row dashboard-panel-head">
              <h3 className="dashboard-panel-title">Recently active users</h3>
              <PlatformButton variant="link" icon="chevR" onClick={() => navigate('/candidates')}>
                View all
              </PlatformButton>
            </div>
            <div>
              {recentUsers.length === 0 ? (
                <p className="dashboard-empty">No active users yet.</p>
              ) : (
                recentUsers.map((activeUser, idx) => {
                  const name = activeUser.name || activeUser.companyName || 'Unknown';
                  const lastActive = activeUser.lastActive
                    ? new Date(activeUser.lastActive).toLocaleDateString()
                    : 'Never';

                  return (
                    <button
                      key={activeUser.id || activeUser.uid || idx}
                      type="button"
                      className="platform-row dashboard-user-row"
                      onClick={() => navigate('/candidates')}
                    >
                      <PlatformAvatar name={name} size={40} photoUrl={activeUser.photoUrl} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="platform-clip" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                          {name}
                        </div>
                        <div className="platform-clip" style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                          Last active
                        </div>
                      </div>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--faint)', flexShrink: 0 }}>
                        {lastActive}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
